import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Mountain, Handshake, Crosshair,
  Wrench, RefreshCw, MapPin,
} from "lucide-react";
import api from "../api";
import { IMAGES, BUSINESSES } from "../siteConfig";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import EnquiryForm from "../components/EnquiryForm";
import { Lifecycle } from "../components/Lifecycle";
import { Reveal, SectionHead, EASE } from "../components/Reveal";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" data-theme="dark" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <img
          src={IMAGES.hero}
          alt="Mining haul truck operating in an open-pit mine"
          className="h-full w-full object-cover img-industrial"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/30" />
      <div className="absolute inset-0 tech-grid opacity-60" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          data-testid="hero-authorised-distributor"
          className="mt-8 flex flex-wrap items-center gap-4 sm:gap-6"
        >
          <img
            src="/assets/brand/bkt-on-dark.png"
            alt="BKT Tyres — Elevate Your Drive (official logo)"
            data-testid="hero-bkt-logo"
            className="h-14 w-auto sm:h-20"
          />
          <h1 className="font-condensed text-4xl sm:text-5xl lg:text-7xl font-bold uppercase leading-[0.98] tracking-tight text-white">
            Authorised <span className="text-ember">BKT</span> Distributor
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-200"
        >
          Three specialised businesses delivering tyres, mining solutions and
          tyre management for the toughest industries.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-9 flex flex-wrap gap-3"
        >
          <a
            href="#businesses"
            data-testid="hero-cta-explore"
            className="inline-flex items-center gap-2 bg-ember px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
          >
            EXPLORE OUR BUSINESSES <ArrowRight className="h-4 w-4" />
          </a>
          <Link
            to="/contact"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
          >
            TALK TO OUR TEAM
          </Link>
        </motion.div>

      </div>
    </section>
  );
}

const WHY_POINTS = [
  { icon: Mountain, title: "Mining Expertise", copy: "Deep working knowledge of open-cast and industrial operating conditions." },
  { icon: Handshake, title: "BKT Tyre Partnership", copy: "Trusted BKT tyre solutions across our territories and segments." },
  { icon: Crosshair, title: "Application-Focused Solutions", copy: "Tyres selected around the machine, the site and the duty cycle." },
  { icon: Wrench, title: "Technical Service", copy: "Support that continues well beyond the point of tyre supply." },
  { icon: RefreshCw, title: "Tyre Lifecycle Management", copy: "Fitment to scrap — tracked, measured and optimised." },
  { icon: MapPin, title: "Strong Regional Presence", copy: "Serving mining and industry across Jharkhand and Eastern India." },
];

const INDUSTRIES = [
  { name: "Mining", copy: "Tyre solutions for demanding mining operations.", image: IMAGES.openPit, q: "Mining" },
  { name: "Earthmoving", copy: "Loaders, graders, excavators and heavy equipment.", image: IMAGES.loader, q: "Earthmoving" },
  { name: "Construction", copy: "Tyres built for demanding construction environments.", image: IMAGES.machinery, q: "Construction" },
  { name: "Commercial Transport", copy: "CVR solutions for trucks, buses and fleet operators.", image: IMAGES.flatbed, q: "Commercial Vehicles" },
  { name: "Industrial", copy: "Tyres for industrial equipment and specialised applications.", image: IMAGES.tread, q: "Industrial" },
  { name: "Fleet Operations", copy: "Data-driven tyre management and performance optimisation.", image: IMAGES.engineer, q: null, to: "/businesses/amr" },
];

const MINING_STEPS = [
  "Tyre Supply", "Tyre Selection", "Site Inspection", "Fitment", "Tyre Monitoring",
  "Repair & Cut Management", "Performance Tracking", "Cost-per-Hour Analysis",
  "Replacement Planning", "Data & Reporting",
];

const PILLARS = [
  { title: "Performance", copy: "Solutions designed around real operating conditions." },
  { title: "Reliability", copy: "Long-term relationships with customers and partners." },
  { title: "Service", copy: "Technical support beyond tyre supply." },
  { title: "Technology", copy: "Data-driven tyre management and operational visibility." },
];

const SEGMENTS = [
  "Mining Contractors", "Fleet Owners", "Infrastructure Companies",
  "Construction Companies", "Industrial Customers", "Commercial Vehicle Fleets",
  "Dealers & Distributors",
];

export default function Home() {
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data.items.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Mangalam Group — Industrial & Mining Tyres, Jharkhand"
        description="Mangalam Group brings together Mangalam Enterprises, Apex Infra and AMR — BKT tyre distribution, OTR & CVR tyres, and tyre management for mining in Jharkhand."
        path="/"
      />

      <Hero />

      <Marquee
        items={["Mining", "Earthmoving", "Construction", "Commercial Transport", "Industrial", "Fleet Operations", "Tyre Management"]}
      />

      {/* Group introduction */}
      <section id="businesses" data-testid="businesses-section" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="The Group"
            title="One Group. Three Specialised Businesses."
            copy="Mangalam Group brings together specialised capabilities across tyres, earthmoving applications and mining tyre management — one integrated industrial ecosystem."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {BUSINESSES.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.1}>
                <Link
                  to={`/businesses/${b.slug}`}
                  data-testid={`business-card-${b.slug}`}
                  className="group block border border-white/10 bg-obsidian-2 transition-colors hover:border-white/25"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={b.image}
                      alt={`${b.name} — ${b.focus}`}
                      className="h-full w-full object-cover img-industrial transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian-2 to-transparent" />
                    <span className="absolute left-4 top-4 font-mono text-[9px] tracking-[0.25em] uppercase px-2 py-1 bg-obsidian/80 backdrop-blur-sm" style={{ color: b.accent }}>
                      {b.badge}
                    </span>
                  </div>
                  <div className="p-6" style={{ borderTop: `2px solid ${b.accent}` }}>
                    <h3 className="font-condensed text-2xl font-bold uppercase tracking-wide text-white">
                      {b.name}
                    </h3>
                    <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">{b.focus}</p>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">{b.blurb}</p>
                    <p className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-ember uppercase">
                      Explore {b.name}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Mangalam */}
      <section data-testid="why-section" className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Why Mangalam Group" title="Built Around Performance" />
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08} className="bg-obsidian-2">
                <div className="h-full p-7">
                  <p.icon className="h-6 w-6 text-ember" strokeWidth={1.5} />
                  <h3 className="mt-5 font-condensed text-xl font-bold uppercase tracking-wide text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Field footage */}
      <section data-testid="footage-section" className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="In the Field"
            title="Where Our Tyres Work"
            copy="Mines, construction sites and highways — the environments our tyres are engineered for, on camera."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { label: "Mining Operations", img: IMAGES.openPit },
              { label: "Construction Sites", img: "https://images.pexels.com/photos/33321431/pexels-photo-33321431.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" },
              { label: "On the Road", img: "https://images.pexels.com/photos/39000647/pexels-photo-39000647.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" },
            ].map((v, i) => (
              <Reveal key={v.label} delay={i * 0.1}>
                <div
                  data-testid={`footage-${v.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative h-72 overflow-hidden border border-white/10"
                >
                  <img
                    src={v.img}
                    alt={`${v.label} — tyres at work`}
                    className="h-full w-full object-cover img-industrial transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                  <p className="absolute bottom-5 left-5 font-condensed text-2xl font-bold uppercase tracking-wide text-white">
                    {v.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BKT partnership */}
      <section data-testid="bkt-section" className="relative overflow-hidden border-y border-white/10 bg-obsidian-2 py-20 lg:py-28">
        <div className="absolute inset-0 tech-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <img
              src="/assets/brand/bkt-consumer-light.png"
              alt="BKT Tyres — Elevate Your Drive (official logo)"
              data-testid="bkt-logo"
              className="h-14 w-auto sm:h-16"
            />
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-ember">Strategic Partnership</p>
            <h2 className="mt-4 font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.98] tracking-tight text-white">
              Powered by <span className="text-ember">BKT</span>
            </h2>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <span data-testid="bkt-official-distributor" className="bg-ember px-4 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#10141B]">
                Official Distributor
              </span>
              <span data-testid="bkt-largest-distributor" className="border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-200">
                Largest Distributor in East India
              </span>
            </div>
            <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-slate-400">
              Mangalam Group is an official BKT distributor — and the largest BKT distributor
              in East India — delivering trusted BKT tyre solutions for demanding applications,
              from OTR and earthmoving to industrial and commercial vehicle radials.
            </p>
            <Link
              to="/bkt"
              data-testid="bkt-cta"
              className="mt-8 inline-flex items-center gap-2 bg-ember px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
            >
              EXPLORE BKT TYRE SOLUTIONS <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative h-80 lg:h-[420px] overflow-hidden border border-white/10">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-tyre-hub/artifacts/ypxgah9n_image.png"
                alt="Aerial view of BKT's Bhuj manufacturing plant, Gujarat"
                data-testid="bkt-bhuj-plant-image"
                className="h-full w-full object-cover img-industrial"
              />
              <div className="absolute bottom-0 left-0 border-t border-r border-white/10 bg-obsidian/85 backdrop-blur-sm px-6 py-4">
                <p className="font-condensed text-3xl font-bold tracking-wide text-white">BKT</p>
                <p className="font-mono text-[9px] tracking-[0.25em] text-slate-400 uppercase">Bhuj Plant · Gujarat</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Product discovery band */}
      <section data-testid="discovery-band" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
            <SectionHead
              eyebrow="Product Discovery"
              title="Find the Right Tyre for Your Application"
              copy="Filter the BKT-powered catalogue by application, equipment, tyre type and size."
            />
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2">
                {["Mining", "Earthmoving", "Construction", "Industrial", "Commercial Vehicles"].map((a) => (
                  <Link
                    key={a}
                    to={`/products?application=${encodeURIComponent(a)}`}
                    data-testid={`quick-filter-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className="border border-white/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-300 transition-colors hover:border-ember hover:text-ember"
                  >
                    {a}
                  </Link>
                ))}
                <Link
                  to="/products"
                  data-testid="quick-filter-search"
                  className="inline-flex items-center gap-2 bg-ember px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[#10141B] transition-colors hover:bg-ember-hover"
                >
                  SEARCH TYRES <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mining solutions ecosystem */}
      <section data-testid="mining-solutions-section" className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Mining Solutions"
            title="Complete Tyre Solutions for Mining"
            copy="We don't just supply tyres. We build solutions around their performance — a complete ecosystem from selection to reporting."
          />
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 grid-cols-2 lg:grid-cols-5">
            {MINING_STEPS.map((step, i) => (
              <Reveal key={step} delay={(i % 5) * 0.06} className="bg-obsidian-2">
                <div className="h-full p-6" data-testid={`mining-step-${i + 1}`}>
                  <p className="font-mono text-3xl font-bold tracking-[0.1em] text-ember">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-condensed text-lg font-bold uppercase leading-tight tracking-wide text-white">
                    {step}
                  </h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AMR technology */}
      <section data-testid="amr-tech-section" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Apex Mineral Resources"
            title="From Tyre Data to Better Decisions"
            copy="AMR tracks every tyre across its full lifecycle — turning inspection data, repairs and running hours into clear decisions for mining operations."
          />
          <div className="mt-14">
            <Reveal><Lifecycle /></Reveal>
          </div>
          <div className="mt-10 max-w-2xl">
            <Reveal>
              <h3 className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
                Digital Tyre Tracking, Built for the Site
              </h3>
              <p className="mt-4 text-base leading-relaxed text-slate-400">
                Fitment records, inspections, cuts and repairs, running hours and cost-per-hour —
                consolidated into a single view of every tyre in the fleet, so maintenance is
                planned instead of reactive.
              </p>
              <Link
                to="/businesses/amr"
                data-testid="amr-cta"
                className="mt-7 inline-flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
              >
                EXPLORE TYRE MANAGEMENT <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Approach pillars */}
      <section data-testid="approach-section" className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Our Approach" title="How We Work" />
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07} className="bg-obsidian-2">
                <div className="h-full p-7">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-ember">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-condensed text-xl font-bold uppercase tracking-wide text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Customer segments */}
      <section data-testid="segments-section" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Who We Serve" title="Built for Serious Operations" />
          <Reveal className="mt-10">
            <div className="flex flex-wrap gap-3">
              {SEGMENTS.map((s) => (
                <span
                  key={s}
                  data-testid={`segment-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="border border-white/15 px-5 py-3 font-condensed text-lg font-medium uppercase tracking-wide text-slate-200"
                >
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Link
              to="/contact"
              data-testid="segments-cta"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] text-ember uppercase"
            >
              DISCUSS YOUR REQUIREMENTS <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>


      {/* Insights preview */}
      <section data-testid="insights-section" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="News & Insights" title="Insights from the Field" />
            <Reveal>
              <Link to="/insights" data-testid="insights-view-all" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] text-ember uppercase">
                VIEW ALL <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {news.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.08}>
                <Link to={`/insights/${n.id}`} data-testid={`insight-card-${n.id}`} className="group block border border-white/10 bg-obsidian-2">
                  <div className="h-44 overflow-hidden">
                    <img src={n.image || IMAGES.tread} alt={n.title} className="h-full w-full object-cover img-industrial transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-ember">{n.category}</p>
                    <h3 className="mt-2 font-condensed text-xl font-bold uppercase leading-snug tracking-wide text-white">
                      {n.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400 line-clamp-2">{n.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section data-testid="enquiry-section" className="relative overflow-hidden border-t border-white/10 py-20 lg:py-28">
        <div className="absolute inset-0">
          <img src={IMAGES.openPit} alt="Open-pit mining operation" className="h-full w-full object-cover img-industrial opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/80 to-obsidian" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
            <SectionHead
              eyebrow="Dealer & Customer Enquiry"
              title="Looking for the Right Tyre Solution?"
              copy="Tell us about your application, equipment and tyre size — our team will respond with a recommendation."
            />
            <Reveal delay={0.1}>
              <EnquiryForm />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
